import React, { useEffect, useMemo, useState } from "react";
import { LoaderCircle, Shield, ShieldCheck, UserCog, Users } from "lucide-react";
import AdminSectionCard from "../components/AdminSectionCard";
import {
  listStaffUsers,
  setUserStaffRole,
  type StaffUser,
} from "../services/staffRoles";

function rolePill(role?: string) {
  if (role === "superadmin") {
    return "border-yellow-400/30 bg-yellow-400/10 text-yellow-100";
  }

  if (role === "admin") {
    return "border-sky-500/30 bg-sky-500/10 text-sky-100";
  }

  return "border-white/10 bg-white/[0.04] text-white/70";
}

export default function OpsStaffRoles() {
  const [users, setUsers] = useState<StaffUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const rows = await listStaffUsers();
        if (!alive) return;
        setUsers(rows);
      } catch (err: any) {
        if (alive) {
          setError(err?.message || "Failed to load staff roles.");
        }
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const staffCounts = useMemo(() => {
    return {
      superadmins: users.filter((user) => user.role === "superadmin").length,
      admins: users.filter((user) => user.role === "admin").length,
      members: users.filter((user) => user.role === "user").length,
    };
  }, [users]);

  async function updateRole(userId: string, role: "user" | "admin") {
    try {
      setBusyUserId(userId);
      setError(null);
      setSuccess(null);
      await setUserStaffRole(userId, role);
      setUsers((current) =>
        current.map((user) => (user.id === userId ? { ...user, role } : user))
      );
      setSuccess(role === "admin" ? "User promoted to admin." : "Admin reverted to user.");
    } catch (err: any) {
      setError(err?.message || "Failed to update role.");
    } finally {
      setBusyUserId(null);
    }
  }

  return (
    <div className="app-page min-h-screen px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-7xl flex-col gap-6">
        <div className="rounded-[2rem] border border-white/10 bg-neutral-950 px-6 py-7">
          <div className="inline-flex items-center gap-2 rounded-full border border-yellow-400/20 bg-yellow-400/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.28em] text-yellow-100">
            <UserCog className="h-3.5 w-3.5" />
            Ops Staff Roles
          </div>
          <h1 className="mt-4 text-4xl font-semibold tracking-tight text-white sm:text-5xl">
            Control who can coach and who can operate the platform
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-6 text-neutral-400 sm:text-base">
            Only accounts with the <span className="text-white">admin</span> role can coach
            classes. Use this screen to promote members into coach admins, while memberships,
            timetable setup, and staff access stay under
            <span className="text-white"> superadmin</span> only.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <div className="app-surface rounded-[1.5rem] border p-5">
            <div className="flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.22em] text-yellow-100">
              <Shield className="h-4 w-4" />
              Superadmins
            </div>
            <div className="mt-3 text-3xl font-semibold text-white">{staffCounts.superadmins}</div>
          </div>
          <div className="app-surface rounded-[1.5rem] border p-5">
            <div className="flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.22em] text-sky-100">
              <ShieldCheck className="h-4 w-4" />
              Admin Coaches
            </div>
            <div className="mt-3 text-3xl font-semibold text-white">{staffCounts.admins}</div>
          </div>
          <div className="app-surface rounded-[1.5rem] border p-5">
            <div className="flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.22em] text-white/70">
              <Users className="h-4 w-4" />
              Members
            </div>
            <div className="mt-3 text-3xl font-semibold text-white">{staffCounts.members}</div>
          </div>
        </div>

        <AdminSectionCard title="Staff Directory">
          <div className="space-y-4">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-sm text-white/70">
              Superadmins are read-only here. Set someone to admin when they should be able to
              coach classes and appear in the timetable coach picker.
            </div>

            {error ? (
              <div className="rounded-2xl border border-red-500/20 bg-red-500/10 p-4 text-sm text-red-200">
                {error}
              </div>
            ) : null}

            {success ? (
              <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/10 p-4 text-sm text-emerald-200">
                {success}
              </div>
            ) : null}

            {loading ? (
              <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-sm text-white/70">
                <LoaderCircle className="h-4 w-4 animate-spin" />
                Loading staff roles...
              </div>
            ) : (
              <div className="space-y-3">
                {users.map((user) => {
                  const isBusy = busyUserId === user.id;
                  const canEdit = user.role !== "superadmin";
                  const label = user.name || user.email || user.id;

                  return (
                    <div
                      key={user.id}
                      className="flex flex-col gap-4 rounded-[1.5rem] border border-white/10 bg-white/[0.03] p-4 lg:flex-row lg:items-center lg:justify-between"
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-3">
                          <div className="truncate text-base font-semibold text-white">{label}</div>
                          <span
                            className={`inline-flex rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${rolePill(
                              user.role
                            )}`}
                          >
                            {user.role || "user"}
                          </span>
                          {user.approvalStatus === "pending" ? (
                            <span className="inline-flex rounded-full border border-amber-500/25 bg-amber-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-200">
                              Pending
                            </span>
                          ) : null}
                        </div>
                        <div className="mt-1 text-sm text-white/55">{user.email || user.id}</div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        {canEdit ? (
                          <>
                            <button
                              type="button"
                              disabled={isBusy || user.role === "user"}
                              onClick={() => updateRole(user.id, "user")}
                              className="rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-white/80 transition hover:bg-white/[0.06] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {isBusy && user.role !== "user" ? "Updating..." : "Set user"}
                            </button>
                            <button
                              type="button"
                              disabled={isBusy || user.role === "admin"}
                              onClick={() => updateRole(user.id, "admin")}
                              className="rounded-full border border-sky-500/25 bg-sky-500/10 px-4 py-2 text-sm font-semibold text-sky-100 transition hover:bg-sky-500/15 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {isBusy && user.role !== "admin" ? "Updating..." : "Make admin coach"}
                            </button>
                          </>
                        ) : (
                          <div className="text-sm text-white/50">Managed outside ops UI</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </AdminSectionCard>
      </div>
    </div>
  );
}
